/**
 * useAuth Hook
 * 
 * Custom React hook for authentication state management.
 * Provides authentication context and methods throughout the application.
 */

import React, { useState, useEffect, useContext, createContext, ReactNode } from 'react';
import { authApi, authUtils, sessionManager } from '../services/auth';
import config from '../config/environment';
import { clearReturnTo } from '../utils/returnTo';

// Type definitions
interface User {
  id: string;
  username: string;
  nickname?: string;
  email: string;
  role: string;
  profile: {
    nickname?: string;
    firstName: string;
    lastName: string;
  };
}


interface AuthContextType {
  // State
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  
  // Methods
  login: (credentials: LoginCredentials) => Promise<any>;
  logout: () => Promise<{ success: boolean }>;
  forgotPassword: (email: string) => Promise<any>;
  resetPassword: (resetData: ResetPasswordData) => Promise<any>;
  refreshUser: () => Promise<User | undefined>;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  updateActivity: () => void;
  
  // Utilities
  authUtils: any;
}

interface LoginCredentials {
  username: string;
  password: string;
  rememberMe?: boolean;
}

interface ResetPasswordData {
  token: string;
  password: string;
  confirmPassword: string;
}

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Default context value
 */
const defaultContextValue: AuthContextType = {
  user: null,
  isLoading: true,
  isAuthenticated: false,
  login: async () => {},
  logout: async () => ({ success: false }),
  forgotPassword: async () => {},
  resetPassword: async () => {},
  refreshUser: async () => undefined,
  hasRole: () => false,
  hasAnyRole: () => false,
  updateActivity: () => {},
  authUtils: null,
};

/**
 * Authentication Context
 */
const AuthContext = createContext<AuthContextType>(defaultContextValue);

/**
 * Authentication Provider Component
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  /**
   * Initialize authentication state
   */
  useEffect(() => {
    const hasFreshToken = (): boolean => {
      const latestToken = authUtils.getStoredToken();
      return !!latestToken && !authUtils.isSessionExpired();
    };

    const initializeAuth = async () => {
      console.log('🔄 initializeAuth started');
      try {
        
        const token = authUtils.getStoredToken();
        console.log('📝 Token from storage:', token ? 'EXISTS' : 'NONE');       
        if (token && !authUtils.isSessionExpired()) {
          // Token exists and is valid, get user data
          console.log('✅ Valid token found, calling getCurrentUser');
          const userData = await authApi.getCurrentUser();
          console.log('👤 userData received:', userData);
          setUser(userData && userData.user ? userData.user : userData);
          setIsAuthenticated(true);
          console.log('✅ User set, isAuthenticated = true');
          sessionManager.startSessionTimer();
        } else {
          console.log('🔄 No valid token, trying cookie auth');
          try {
            const userData = await authApi.getCurrentUser();
            console.log('👤 Cookie auth userData:', userData);
            setUser(userData && userData.user ? userData.user : userData);
            setIsAuthenticated(true);
            console.log('✅ Cookie auth success, isAuthenticated = true');
            sessionManager.startSessionTimer();
          } catch (error) {
            console.log('❌ Cookie auth failed:', error);
            // No cookie auth either. Avoid clobbering a token that may have
            // been stored by a concurrent successful login in another flow.
            if (!hasFreshToken()) {
              authUtils.clearStoredToken();
              setUser(null);
              setIsAuthenticated(false);
            }
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        // Preserve freshly stored tokens on transient backend/network errors.
        if (!hasFreshToken()) {
          authUtils.clearStoredToken();
          setUser(null);
          setIsAuthenticated(false);
        }
      } finally {
        setIsLoading(false);
        console.log('🏁 initializeAuth finished, isLoading = false');
      }
    };

    initializeAuth();

    // Cleanup on unmount
    return () => {
      sessionManager.clearSessionTimer();
    };
  }, []);

  /**
   * Login function
   */
  const login = async (credentials: LoginCredentials) => {
    try {
      console.log('useAuth login called with credentials:', credentials);
      setIsLoading(true);
      
      const response = await authApi.login({
        username: credentials.username,
        password: credentials.password,
        rememberMe: credentials.rememberMe || false,
      });
      
      console.log('useAuth login successful:', response);
      
      // Store token
      authUtils.storeToken(response.token, credentials.rememberMe || false);
      
      // Update state
      setUser(response.user);
      setIsAuthenticated(true);
      
      // Start session timer
      sessionManager.startSessionTimer();
      
      return response;
    } catch (error) {
      console.error('useAuth login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Logout function
   */
  const logout = async () => {
    // A signed-out user's next sign-in goes to the dashboard, not a page saved earlier.
    clearReturnTo();
    try {
      setIsLoading(true);
      
      await authApi.logout();
      
      // Clear state
      setUser(null);
      setIsAuthenticated(false);
      
      // Clear session timer
      sessionManager.clearSessionTimer();
      
      return { success: true };
    } catch (error) {
      // Even if logout fails, clear local state
      setUser(null);
      setIsAuthenticated(false);
      sessionManager.clearSessionTimer();
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Forgot password function
   */
  const forgotPassword = async (email: string) => {
    try {
      setIsLoading(true);
      const response = await authApi.forgotPassword(email);
      return response;
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Reset password function
   */
  const resetPassword = async (resetData: ResetPasswordData) => {
    try {
      setIsLoading(true);
      const response = await authApi.resetPassword(resetData);
      return response;
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Refresh user data
   */
  const refreshUser = async (): Promise<User | undefined> => {
    try {
      if (!isAuthenticated) return;
      
      const userData = await authApi.getCurrentUser();
      const normalized = userData && userData.user ? userData.user : userData;
      setUser(normalized);
      return normalized;
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      throw error;
    }
  };

  /**
   * Check if user has specific role
   */
  const hasRole = (role: string): boolean => {
    return user?.role === role;
  };

  /**
   * Check if user has any of the specified roles
   */
  const hasAnyRole = (roles: string[]): boolean => {
    return roles.includes(user?.role || '');
  };

  /**
   * Update user activity (reset session timer)
   */
  const updateActivity = (): void => {
    if (isAuthenticated) {
      sessionManager.resetSessionTimer();
    }
  };

  // Context value
  const value: AuthContextType = {
    // State
    user,
    isLoading,
    isAuthenticated,
    
    // Methods
    login,
    logout,
    forgotPassword,
    resetPassword,
    refreshUser,
    hasRole,
    hasAnyRole,
    updateActivity,
    
    // Utilities
    authUtils,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * useAuth Hook
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

/**
 * Higher-Order Component for authentication protection
 */
export const withAuth = (WrappedComponent: React.ComponentType<any>, options: { roles?: string[]; redirectTo?: string } = {}) => {
  const { roles = [], redirectTo = '/login' } = options;
  
  return function AuthenticatedComponent(props: any) {
    const { isAuthenticated, isLoading, hasAnyRole } = useAuth();
    
    useEffect(() => {
      if (!isLoading && !isAuthenticated) {
        window.location.href = redirectTo;
      } else if (!isLoading && isAuthenticated && roles.length > 0 && !hasAnyRole(roles)) {
        window.location.href = '/unauthorized';
      }
    }, [isAuthenticated, isLoading, hasAnyRole]);
    
    if (isLoading) {
      return <div>Loading...</div>;
    }
    
    if (!isAuthenticated) {
      return null;
    }
    
    if (roles.length > 0 && !hasAnyRole(roles)) {
      return <div>Access Denied</div>;
    }
    
    return <WrappedComponent {...props} />;
  };
};

/**
 * Hook for protected routes
 */
export const useAuthGuard = (options: { roles?: string[]; redirectTo?: string } = {}) => {
  const { roles = [], redirectTo = '/login' } = options;
  const { isAuthenticated, isLoading, hasAnyRole } = useAuth();
  
  const canAccess = isAuthenticated && (roles.length === 0 || hasAnyRole(roles));
  const shouldRedirect = !isLoading && (!isAuthenticated || (roles.length > 0 && !hasAnyRole(roles)));
  
  useEffect(() => {
    if (shouldRedirect) {
      if (!isAuthenticated) {
        window.location.href = redirectTo;
      } //else if (roles.length > 0 && !hasAnyRole(roles)) {
        //window.location.href = '/unauthorized';
      //}
    }
  }, [shouldRedirect, isAuthenticated, hasAnyRole, redirectTo, roles]);
  
  return {
    canAccess,
    isLoading,
    shouldRedirect,
  };
};

export default useAuth;
